import React from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Bug, Building2, CalendarCheck, Cookie, LogOut, PanelRightClose, PanelRightOpen, Trophy, User as UserIcon } from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { useAuth } from '@/lib/AuthContext'
import { useRoute } from '@/lib/RouteContext'
import { useCommunity } from '@/lib/CommunityContext'
import { ROUTES } from '@/lib/routes'
import { ORGANIZING_EVENTS_PATH } from '@/lib/eventKioskPath'
import { useInMyCompany } from '@/components/features/companies/MyCompanyLink'
import { useOrganizesEvents } from '@/components/features/events/checkin/OrganizingEventsLink'
import { useOptionalChat, useChatBadge, OPEN_CHAT_EVENT } from '@/components/features/chat/ChatProvider'
import { ChatInbox, type ChatInboxTab, type ChatInboxStatusSlot } from '@/components/features/chat/ChatInbox'
import { ChatSettingsMenu } from '@/components/features/chat/ChatSettingsMenu'
import { MyStatusDot, StatusSelect } from '@/components/features/chat/ChatStatus'
import { MY_STATUS_KEY } from '@/components/features/chat/chatStatus'
import { useSwipeToClose } from '@/components/features/chat/sidePanel'
import { PushPanelItem } from '@/components/features/push/PushPanelItem'
import { cn } from '@/lib/utils'
import { useHasCookieChoice } from '@/components/features/cookies/useHasCookieChoice'
import { openCookieNotice } from '@/lib/cookieConsent'
import { useCommunityNavigation } from '@/lib/navigation'
import { ReportProblemDialog } from '@/components/shared/ReportProblem'
import { useSidebar } from '@/lib/SidebarContext'
import { usePersonalLinks } from '@/components/layout/PersonalLinks'

/** A compact text button of the avatar panel (icon + a short label). */
const PANEL_LABELED_BUTTON = 'inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md px-2 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'

interface Props {
  /** The dot on the avatar's bottom-right corner; by default my chat status (none while the chat is off). */
  avatarBadge?: React.ReactNode
  /** Beside Conversas | Pessoas, right before the list; by default the chat status picker (none while the chat is off). */
  statusSlot?: ChatInboxStatusSlot
  /** Extra words for the trigger's `aria-label`; by default the chat status. */
  labelSuffix?: string
  className?: string
}

/**
 * F-184 (David: "clicar na minha foto e ele abrir a sidebar de chat"): the
 * header avatar is the one door to "me" and to Mensagens. It carries the unread
 * badge (top right; the bottom right is left for the status dot of A2) and opens
 * a full-height sidebar on the right (full screen below 600 px): the "me" line
 * (chat gear · Minha conta · Sair), a row with the sidebar's old personal group
 * (Minha empresa · Eventos que organizo · Engajamento geral, each only for whom
 * it applies) and, when the community has Mensagens on, Conversas | Pessoas
 * (`ChatInbox`) with my status beside the switch. "Ver meu perfil" lives in
 * Minha conta since 2026-10-29. Picking someone
 * opens the docked window (desktop) or the full-screen conversation, and closes
 * the sidebar. There is no separate chat icon in the header any more.
 */
export const UserMenu = ({ avatarBadge: avatarBadgeProp, statusSlot: statusSlotProp, labelSuffix: labelSuffixProp, className }: Props) => {
  const { t } = useTranslation(['navigation', 'chat', 'eventCheckIn', 'common'])
  const { user, logout } = useAuth()
  const { community } = useCommunity()
  const hasCookieChoice = useHasCookieChoice()
  /* The personal group of the sidebar moved here (David, 2026-10-29): the same rules decide who sees each. */
  const inCompany = useInMyCompany()
  /* With the bar on top (F-191 phase 5) the side menu is hidden, so its personal block lives here. */
  const { isTopNav } = useSidebar()
  const personalLinks = usePersonalLinks()
  const topNavLinks = isTopNav ? personalLinks : []
  /* F-191 phase 5: shortcuts the community hides for everyone (§4.5.1); the pages stay reachable by URL. */
  const { hiddenNative } = useCommunityNavigation()
  const showOrganizing = useOrganizesEvents() && !hiddenNative.includes('organizedEvents')
  const showRanking = Boolean(community?.globalRanking) && !hiddenNative.includes('leaderboard')
  const peopleHidden = hiddenNative.includes('people')
  /* "Reportar problema" hidden from the header moves into this panel. */
  const reportHere = hiddenNative.includes('reportProblem')
  const [reportOpen, setReportOpen] = React.useState(false)
  const { navigate } = useRoute()
  const chat = useOptionalChat()
  const chatOn = Boolean(chat?.enabled)
  const unread = useChatBadge()
  /* F-184: my status, only while the community has Mensagens on. */
  const avatarBadge = avatarBadgeProp ?? (chatOn ? <MyStatusDot /> : null)
  const statusSlot = statusSlotProp ?? (chatOn ? (compact: boolean) => <StatusSelect align="end" compact={compact} /> : null)
  const labelSuffix = labelSuffixProp ?? (chatOn && chat ? `${t('status.label', { ns: 'chat' })}: ${t(MY_STATUS_KEY[chat.myShownStatus], { ns: 'chat' })}` : undefined)
  const [open, setOpen] = React.useState(false)
  const [tab, setTab] = React.useState<ChatInboxTab>('people')
  const [blocksOpen, setBlocksOpen] = React.useState(false)
  const triggerRef = React.useRef<HTMLButtonElement | null>(null)
  const panelRef = React.useRef<HTMLDivElement | null>(null)
  const wasOpen = React.useRef(false)
  const close = React.useCallback(() => setOpen(false), [])
  const swipe = useSwipeToClose(close)

  /* The docked windows move beside the panel while it is open (desktop). */
  const setSidePanelOpen = chat?.setSidePanelOpen
  React.useEffect(() => {
    setSidePanelOpen?.(open)
  }, [open, setSidePanelOpen])
  React.useEffect(() => () => setSidePanelOpen?.(false), [setSidePanelOpen])

  /* Links and other screens ask for the chat on a tab (`/_/chat/connections` → Pessoas). */
  React.useEffect(() => {
    const onOpen = (event: Event) => {
      const wanted = (event as CustomEvent<{ tab?: ChatInboxTab }>).detail?.tab
      if (wanted) setTab(wanted)
      setOpen(true)
    }
    window.addEventListener(OPEN_CHAT_EVENT, onOpen)
    return () => window.removeEventListener(OPEN_CHAT_EVENT, onOpen)
  }, [])

  /* Esc closes; the focus goes into the sidebar on open and back to the avatar on close. */
  React.useEffect(() => {
    if (open) {
      wasOpen.current = true
      /* Into the panel: its first control (the chat gear, or Minha conta with the chat off). */
      panelRef.current?.querySelector<HTMLElement>('button, input')?.focus()
      const onKey = (e: KeyboardEvent) => {
        if (e.key !== 'Escape' || e.defaultPrevented) return
        /* An Esc meant for a menu inside the sidebar (the chat's ⋯) only closes that menu. */
        if (document.querySelector('[role="menu"]')) return
        setOpen(false)
      }
      /* A press outside the panel and the avatar closes it (the backdrop covers the page; this covers the header). */
      const onPointer = (e: PointerEvent) => {
        const target = e.target as Element | null
        if (!target || triggerRef.current?.contains(target) || document.getElementById('user-panel')?.contains(target)) return
        /* Menus and dialogs portaled from inside the panel (the ⋯, the status picker, a confirmation). */
        if (target.closest('[data-radix-popper-content-wrapper], [role="menu"], [role="dialog"], [role="alertdialog"]')) return
        /* The docked conversation windows live beside the open panel (desktop): using them keeps it open. */
        if (target.closest('[data-chat-window], [data-chat-overflow]')) return
        setOpen(false)
      }
      window.addEventListener('keydown', onKey)
      document.addEventListener('pointerdown', onPointer)
      return () => {
        window.removeEventListener('keydown', onKey)
        document.removeEventListener('pointerdown', onPointer)
      }
    }
    setBlocksOpen(false)
    if (wasOpen.current) {
      wasOpen.current = false
      triggerRef.current?.focus()
    }
  }, [open])

  if (!user) return null

  const toggle = () => {
    /* David: unread messages open on Conversas, otherwise on Pessoas. */
    if (!open) setTab(unread > 0 || peopleHidden ? 'conversations' : 'people')
    setOpen((v) => !v)
  }
  /* David: on desktop picking someone keeps the panel open (the window docks to its left); below 1024 px the conversation takes the screen. */
  const keepOpenOnDesktop = () => {
    if (window.innerWidth < 1024) setOpen(false)
  }
  const go = (action: () => void) => () => {
    setOpen(false)
    action()
  }
  const signOut = () => {
    logout()
    navigate('/')
  }

  const label = [
    chatOn ? t('messagesAndAccount', { ns: 'navigation' }) : t('accountMenu', { ns: 'navigation' }),
    unread > 0 ? t('unreadCount', { ns: 'chat', count: unread }) : '',
    labelSuffix,
  ].filter(Boolean).join(', ')

  const iconLinkClasses = 'inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="user-panel"
        title={!chatOn ? t('accountMenu', { ns: 'navigation' }) : open ? t('closePanel', { ns: 'navigation' }) : t('openPanel', { ns: 'navigation' })}
        data-user-menu-trigger
        data-state={open ? 'open' : 'closed'}
        className={cn(
          /* No resting background (like the globe and the bell); pr-2 puts the panel icon's right edge 24 px (20 px
             below sm) from the window, in one column with Sair in the panel (px-4 / px-3 + half of its 32 px button). */
          'flex items-center gap-2 rounded-md py-1 pl-2 pr-2 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          className
        )}
      >
        {/* APP-69: below `sm` the centered community name has no room beside the
            person's name, so only the avatar stays (it carries the name in `alt`). */}
        <span className="hidden flex-col items-end sm:flex">
          <span className="text-sm text-gray-600">{user.name}</span>
          {user.isSuperAdmin && (
            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-yellow-100 text-yellow-700 border border-yellow-200">
              {t('superAdmin', { ns: 'navigation' })}
            </span>
          )}
        </span>
        <span className="relative h-8 w-8 rounded-full">
          <Avatar src={user.avatar} alt={user.name} fallback={user.name} size="sm" />
          {unread > 0 && (
            <span
              className="absolute -right-1.5 -top-1.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white"
              data-user-menu-badge
              aria-hidden
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
          {avatarBadge}
        </span>
        {/* The affordance: this opens a panel; it flips while the panel is open (David, 2026-10-29). */}
        {open
          ? <PanelRightClose className="h-4 w-4 text-gray-500" aria-hidden data-user-menu-chevron="close" />
          : <PanelRightOpen className="h-4 w-4 text-gray-500" aria-hidden data-user-menu-chevron="open" />}
      </button>
      {/* A portal: the header's backdrop blur would otherwise trap a fixed child inside it. The panel opens
          under the header (top-16, z-40 below the header's z-50), so the avatar stays put and closes it again. */}
      {open && createPortal(
        <>
          {/* Tablet only: on desktop the page and the docked windows stay usable beside the panel (a click on the page closes it). */}
          <div className="fixed inset-x-0 bottom-0 top-16 z-40 hidden bg-black/30 min-[600px]:block lg:hidden" onClick={close} aria-hidden data-user-panel-backdrop />
          <div
            id="user-panel"
            ref={panelRef}
            role="dialog"
            aria-label={chatOn ? t('messagesAndAccount', { ns: 'navigation' }) : t('accountMenu', { ns: 'navigation' })}
            data-user-panel
            className="fixed inset-x-0 bottom-0 top-16 z-40 flex flex-col border-t border-gray-200 bg-white min-[600px]:left-auto min-[600px]:w-[380px] min-[600px]:border-l min-[600px]:shadow-[-8px_0_32px_rgba(15,23,42,0.14)]"
            {...swipe}
          >
            {/* One compact line (David: the header avatar already says who you are), on the right: the chat settings gear
                (icon only), "Minha conta" as a small labeled button, then Sair, last, under the header's panel icon. My status
                moved down beside Conversas | Pessoas (David, 2026-10-29); the room on the left is for one more labeled entry
                (Agenda, being designed). Closing is the avatar, Esc or a click outside. */}
            <div className="flex shrink-0 items-center justify-end gap-1 border-b border-gray-200 px-3 py-1.5 sm:px-4" data-user-panel-me>
              {chatOn && <ChatSettingsMenu className="shrink-0" onOpenBlocks={() => { setBlocksOpen(true); setTab('conversations') }} />}
              <nav className="flex shrink-0 items-center gap-1" aria-label={t('accountMenu', { ns: 'navigation' })}>
                <button type="button" className={PANEL_LABELED_BUTTON} onClick={go(() => navigate('/account'))} data-user-menu-item="account">
                  <UserIcon className="h-4 w-4 shrink-0" aria-hidden />
                  <span>{t('myAccount', { ns: 'navigation' })}</span>
                </button>
                <button type="button" className={cn(iconLinkClasses, 'text-red-600 hover:bg-red-50 hover:text-red-700')} onClick={go(signOut)} title={t('signOut', { ns: 'navigation' })} aria-label={t('signOut', { ns: 'navigation' })} data-user-menu-item="signout">
                  <LogOut className="h-4 w-4" />
                </button>
              </nav>
            </div>
            {/* The personal group of the sidebar (David, 2026-10-29), each only for whom it applies (same rules as before). */}
            {(topNavLinks.length > 0 || inCompany || showOrganizing || showRanking || hasCookieChoice || reportHere) && (
              <div className="flex shrink-0 flex-wrap items-center gap-1 border-b border-gray-200 px-3 py-1.5 sm:px-4" data-user-panel-personal>
                {topNavLinks.map((link) => (
                  <button key={link.key} type="button" className={PANEL_LABELED_BUTTON} onClick={go(() => navigate(link.path))} data-user-menu-item={`personal-${link.key}`}>
                    <link.icon className="h-4 w-4 shrink-0" aria-hidden />
                    <span>{link.label}</span>
                  </button>
                ))}
                {inCompany && (
                  <button type="button" className={PANEL_LABELED_BUTTON} onClick={go(() => navigate(ROUTES.myCompany))} data-user-menu-item="my-company" data-testid="my-company-link">
                    <Building2 className="h-4 w-4 shrink-0" aria-hidden />
                    <span>{t('myCompany', { ns: 'navigation' })}</span>
                  </button>
                )}
                {showOrganizing && (
                  <button type="button" className={PANEL_LABELED_BUTTON} onClick={go(() => navigate(ORGANIZING_EVENTS_PATH))} data-user-menu-item="organizing-events" data-testid="organizing-events-link">
                    <CalendarCheck className="h-4 w-4 shrink-0" aria-hidden />
                    <span>{t('organizing.menu', { ns: 'eventCheckIn' })}</span>
                  </button>
                )}
                {showRanking && (
                  <button type="button" className={PANEL_LABELED_BUTTON} onClick={go(() => navigate('/ranking'))} data-user-menu-item="ranking">
                    <Trophy className="h-4 w-4 shrink-0" aria-hidden />
                    <span>{t('generalRanking', { ns: 'navigation' })}</span>
                  </button>
                )}
                {reportHere && (
                  <button type="button" className={PANEL_LABELED_BUTTON} onClick={go(() => setReportOpen(true))} data-user-menu-item="report-problem">
                    <Bug className="h-4 w-4 shrink-0" aria-hidden />
                    <span>{t('reportProblem.menu', { ns: 'common' })}</span>
                  </button>
                )}
                {/* F-201: reopens the cookie notice (only for a community with its own GA). */}
                {hasCookieChoice && (
                  <button type="button" className={PANEL_LABELED_BUTTON} onClick={go(openCookieNotice)} data-user-menu-item="cookies" data-testid="user-menu-cookies">
                    <Cookie className="h-4 w-4 shrink-0" aria-hidden />
                    <span>{t('cookies.link', { ns: 'common' })}</span>
                  </button>
                )}
              </div>
            )}
            {/* F-186: install / notifications, until resolved on this device. */}
            <PushPanelItem onAction={close} />
            {chatOn && (
              <div className="min-h-0 flex-1">
                <ChatInbox hidePeople={peopleHidden} tab={tab} onTabChange={setTab} onPicked={keepOpenOnDesktop} showSettings={false} blocksOpen={blocksOpen} onBlocksOpenChange={setBlocksOpen} statusSlot={statusSlot} />
              </div>
            )}
          </div>
        </>,
        document.body
      )}
      {reportHere && <ReportProblemDialog open={reportOpen} onOpenChange={setReportOpen} />}
    </>
  )
}
